// Which APNG the anvil character ([code]smith) plays. The PNGs live in this
// package's assets/anvil-animations; consumers turn the file name from
// `anvilAnimationFile` into a URL for wherever they serve that folder.

import type {
  AnvilMaterializeStyle,
  AnvilTransitionVariant,
} from "./manifest.js";

// The seeded `active` pool: the hand-drawn set plus programmatic variants
// generated by scripts/generate-anvil-variants.mjs (eureka, on-fire, orbiting,
// sleuthing, swimming, zen). `caffeinating` is generated too but deliberately
// absent here: it is bound to waiting on sub-agents, so drawing it at random
// would drain the signal.
export const ANIMATION_VARIANTS = [
  "beautiful-mind",
  "concentrate",
  "considering",
  "doomscrolling",
  "eureka",
  "on-fire",
  "orbiting",
  "pong",
  "pondering",
  "ruminating",
  "running",
  "sleuthing",
  "swimming",
  "thinking",
  "zen",
] as const;

export type AnvilActiveVariant = (typeof ANIMATION_VARIANTS)[number];

export function isAnvilActiveVariant(
  value: unknown,
): value is AnvilActiveVariant {
  return (ANIMATION_VARIANTS as ReadonlyArray<unknown>).includes(value);
}

/**
 * - `active`: seeded pick from {@link ANIMATION_VARIANTS} (right after user input)
 * - `type`: typing while prose or file content streams in
 * - `caffeinating`: coffee break while sub-agents carry the turn
 * - `sipping`: the drink itself, an occasional beat inside `caffeinating`
 * - `idle`: subdued loop while tools or text are visibly active
 * - `ruminating`: personality loop while thinking streams and in quiet gaps
 * - `sleeping`: asleep until a scheduled wake-up resumes the session
 */
export type AnvilAnimationVariant =
  | "active"
  | "type"
  | "idle"
  | "ruminating"
  | "caffeinating"
  | "sipping"
  | "sleeping";

export type AnvilTheme = "light" | "dark";

/**
 * A PNG the anvil rests on: a manifest loop with intro/outro transitions, or
 * the play-once sip, which hands off straight to and from the held mug.
 */
export type AnvilLoopName = AnvilTransitionVariant | "caffeinating-sip";

/** The one PNG each non-seeded variant maps to. */
const VARIANT_ASSETS: Record<
  Exclude<AnvilAnimationVariant, "active">,
  AnvilLoopName
> = {
  type: "type",
  idle: "idle",
  ruminating: "ruminating",
  caffeinating: "caffeinating-hold",
  sipping: "caffeinating-sip",
  sleeping: "sleeping",
};

/** FNV-1a hash of `seed` folded into `[0, modulo)`. */
export function seededIndex(seed: string, modulo: number): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % modulo;
}

/**
 * Seed-stable pick from the `active` PNG set. Every surface that renders the
 * anvil character for the same turn (transcript indicator, scroll-to-bottom
 * pill) resolves through this so they all show the same animation.
 */
export function seededAnvilActiveVariant(seed: string): AnvilActiveVariant {
  return ANIMATION_VARIANTS[
    seededIndex(`${seed}:variant`, ANIMATION_VARIANTS.length)
  ]!;
}

/**
 * The PNG an anvil variant rests on, e.g. `("active", "thinking")` →
 * `thinking` and `("caffeinating", …)` → `caffeinating-hold`.
 * `activeVariant` is only consulted when `animationVariant` is `active`.
 */
export function anvilLoopName(
  animationVariant: AnvilAnimationVariant,
  activeVariant: AnvilActiveVariant,
): AnvilLoopName {
  return animationVariant === "active"
    ? activeVariant
    : VARIANT_ASSETS[animationVariant];
}

/** File name of an anvil animation, e.g. `anvil-thinking-dark.png`; see {@link anvilLoopName}. */
export function anvilAnimationFile(
  animationVariant: AnvilAnimationVariant,
  activeVariant: AnvilActiveVariant,
  theme: AnvilTheme,
): string {
  return `anvil-${anvilLoopName(animationVariant, activeVariant)}-${theme}.png`;
}

export const ANVIL_MATERIALIZE_STYLES = [
  "outline",
  "random",
  "scanline",
] as const satisfies readonly AnvilMaterializeStyle[];

export function seededAnvilMaterializeStyle(
  seed: string,
): AnvilMaterializeStyle {
  return ANVIL_MATERIALIZE_STYLES[
    seededIndex(`${seed}:materialize`, ANVIL_MATERIALIZE_STYLES.length)
  ]!;
}

export const ANVIL_MATERIALIZE_ASSETS = ANVIL_MATERIALIZE_STYLES.map(
  (style) => `materialize-${style}`,
);

/** Every PNG the working indicator can reach, for prefetch. `neutral` is the pose between an outro and the next intro. */
export const ANVIL_ANIMATION_ASSETS = Array.from(
  new Set([
    ...ANIMATION_VARIANTS,
    ...Object.values(VARIANT_ASSETS),
    "neutral",
    ...ANVIL_MATERIALIZE_ASSETS,
  ]),
);
